/**
 * API Service: Dashboard
 * Service for dashboard API calls using centralized Axios-based API service
 *
 * API Endpoints (from Postman collection):
 * - GET /product/api/v1/dashboard/summary
 * - GET /product/api/v1/dashboard/user-tiers
 * - GET /product/api/v1/dashboard/transaction-activity
 * - GET /product/api/v1/dashboard/merchant-points
 * - GET /product/api/v1/dashboard/city-transactions
 * - GET /product/api/v1/dashboard/demographics/gender
 * - GET /product/api/v1/dashboard/demographics/age
 * - GET /product/api/v1/dashboard/summary-details
 */

import { config } from '@/config';
import { apiService, type ApiResponse } from '@/remote/api';
import type {
  FetchDashboardResponse,
  DashboardSummary,
  UserTiersApiResponse,
  TransactionActivityApiResponse,
  MerchantPointsApiResponse,
  CityTransactionsApiResponse,
  GenderDemographicsData,
  AgeRangeData,
  ReportDetailApiResponse,
} from '../response';

// Mock data imports (used when VITE_USE_MOCK_DATA=true)
import {
  mockTransactionActivityData,
  mockMerchantPointsData,
  mockTopTransactionsCityData,
  mockUserGenderData,
  mockUserAgeData,
  getMockReportDetailData,
  mockUserTierData,
} from '../../../mock';

/**
 * Mock data for the main dashboard response
 * Updated to match new API response structure from Postman collection
 */
const mockDashboardResponse: FetchDashboardResponse = {
  widgets: [
    {
      id: '1',
      title: 'Total Users',
      type: 'metric',
      size: 'small',
      data: 1234,
      description: 'Total registered users',
      lastUpdated: new Date().toISOString(),
    },
    {
      id: '2',
      title: 'Active Users',
      type: 'metric',
      size: 'small',
      data: 856,
      description: 'Users active in the last 30 days',
      lastUpdated: new Date().toISOString(),
    },
  ],
  user: {
    id: '1',
    name: 'John Doe',
    email: 'john.doe@example.com',
    role: 'admin',
  },
  navigationItems: [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/dashboard', isActive: true },
    { id: 'analytics', label: 'Analytics', icon: '📈', path: '/analytics', isActive: false },
    { id: 'reports', label: 'Reports', icon: '📄', path: '/reports', isActive: false },
    { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings', isActive: false },
  ],
  summary: {
    userRegistered: { currentValue: 17, previousValue: 0, growthPercentage: 0 },
    totalPoints: { currentValue: 560000, previousValue: 390000, growthPercentage: 43.59 },
    registeredMerchant: { currentValue: 3, previousValue: 0, growthPercentage: 0 },
    voucherRedeemed: { currentValue: 7, previousValue: 3, growthPercentage: 133.33 },
  },
  userTiers: {
    totalUsers: mockUserTierData.totalUsers,
    distribution: [...mockUserTierData.distribution],
  },
  transactionActivity: mockTransactionActivityData.data,
  merchantPoints: [...mockMerchantPointsData.data],
  cityTransactions: [...mockTopTransactionsCityData.data],
  genderDemographics: mockUserGenderData,
  ageDemographics: [...mockUserAgeData.data],
  reportDetail: {
    items: [...getMockReportDetailData(1, 10).data],
    paging: getMockReportDetailData(1, 10).paging,
  },
};

export class DashboardApiService {
  private readonly useMockData: boolean;

  constructor() {
    this.useMockData = config.features.useMockData;
  }

  /**
   * Fetch main dashboard data by calling all 8 dashboard endpoints in parallel
   * Uses centralized apiService for all HTTP requests
   * Aggregates responses from:
   * - GET /product/api/v1/dashboard/summary
   * - GET /product/api/v1/dashboard/user-tiers
   * - GET /product/api/v1/dashboard/transaction-activity
   * - GET /product/api/v1/dashboard/merchant-points
   * - GET /product/api/v1/dashboard/city-transactions
   * - GET /product/api/v1/dashboard/demographics/gender
   * - GET /product/api/v1/dashboard/demographics/age
   * - GET /product/api/v1/dashboard/summary-details
   */
  public async fetchDashboard(): Promise<FetchDashboardResponse> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockDashboardResponse), 500);
      });
    }

    // Make parallel API calls to all 8 dashboard endpoints using centralized apiService
    // Use getRaw<T> since API responses are already in the expected format (data + meta)
    const [
      summaryResponse,
      userTiersResponse,
      transactionActivityResponse,
      merchantPointsResponse,
      cityTransactionsResponse,
      genderDemographicsResponse,
      ageDemographicsResponse,
      reportDetailResponse,
    ] = await Promise.all([
      apiService.get<DashboardSummary>('/product/api/v1/dashboard/summary'),
      apiService.getRaw<UserTiersApiResponse>('/product/api/v1/dashboard/user-tiers'),
      apiService.getRaw<TransactionActivityApiResponse>('/product/api/v1/dashboard/transaction-activity'),
      apiService.getRaw<MerchantPointsApiResponse>('/product/api/v1/dashboard/merchant-points'),
      apiService.getRaw<CityTransactionsApiResponse>('/product/api/v1/dashboard/city-transactions'),
      apiService.get<GenderDemographicsData>('/product/api/v1/dashboard/demographics/gender'),
      apiService.get<AgeRangeData[]>('/product/api/v1/dashboard/demographics/age'),
      apiService.getRaw<ReportDetailApiResponse>('/product/api/v1/dashboard/summary-details'),
    ]);

    // Aggregate all responses into a single FetchDashboardResponse
    return {
      // Use static navigation and widget data (can be fetched from separate endpoint if needed)
      widgets: mockDashboardResponse.widgets,
      user: mockDashboardResponse.user,
      navigationItems: mockDashboardResponse.navigationItems,
      // Real data from API endpoints
      summary: summaryResponse.data,
      userTiers: {
        totalUsers: userTiersResponse.data.totalUsers,
        distribution: [...userTiersResponse.data.distribution],
      },
      transactionActivity: transactionActivityResponse.data,
      merchantPoints: [...merchantPointsResponse.data],
      cityTransactions: [...cityTransactionsResponse.data],
      genderDemographics: genderDemographicsResponse.data,
      ageDemographics: [...ageDemographicsResponse.data],
      reportDetail: {
        items: [...reportDetailResponse.data],
        paging: reportDetailResponse.paging,
      },
    };
  }

  /**
   * Fetch dashboard summary metrics
   * Endpoint: GET /product/api/v1/dashboard/summary
   */
  public async fetchSummary(): Promise<DashboardSummary> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockDashboardResponse.summary), 300);
      });
    }

    return apiService.getData<DashboardSummary>('/product/api/v1/dashboard/summary');
  }

  /**
   * Fetch user tier distribution data
   * Endpoint: GET /product/api/v1/dashboard/user-tiers
   */
  public async fetchUserTiers(): Promise<UserTiersApiResponse> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({
          data: mockUserTierData,
          meta: { code: '2000', message: 'OK' },
        }), 300);
      });
    }

    return apiService.getRaw<UserTiersApiResponse>('/product/api/v1/dashboard/user-tiers');
  }

  /**
   * Fetch transaction activity chart data
   * Endpoint: GET /product/api/v1/dashboard/transaction-activity
   */
  public async fetchTransactionActivity(): Promise<TransactionActivityApiResponse> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockTransactionActivityData), 300);
      });
    }

    return apiService.getRaw<TransactionActivityApiResponse>('/product/api/v1/dashboard/transaction-activity');
  }

  /**
   * Fetch merchant points data
   * Endpoint: GET /product/api/v1/dashboard/merchant-points
   */
  public async fetchMerchantPoints(): Promise<MerchantPointsApiResponse> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockMerchantPointsData), 300);
      });
    }

    return apiService.getRaw<MerchantPointsApiResponse>('/product/api/v1/dashboard/merchant-points');
  }

  /**
   * Fetch city transactions data
   * Endpoint: GET /product/api/v1/dashboard/city-transactions
   */
  public async fetchCityTransactions(): Promise<CityTransactionsApiResponse> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockTopTransactionsCityData), 300);
      });
    }

    return apiService.getRaw<CityTransactionsApiResponse>('/product/api/v1/dashboard/city-transactions');
  }

  /**
   * Fetch user gender demographics data
   * Endpoint: GET /product/api/v1/dashboard/demographics/gender
   * Note: Postman collection has a typo "gende" but using correct "gender"
   */
  public async fetchUserGenderDemographics(): Promise<ApiResponse<GenderDemographicsData>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({
          data: mockUserGenderData,
          meta: { code: '20000', message: 'ok' },
        }), 300);
      });
    }

    return apiService.get<GenderDemographicsData>('/product/api/v1/dashboard/demographics/gender');
  }

  /**
   * Fetch user age demographics data
   * Endpoint: GET /product/api/v1/dashboard/demographics/age
   */
  public async fetchUserAgeDemographics(): Promise<ApiResponse<AgeRangeData[]>> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({
          data: [...mockUserAgeData.data],
          meta: { code: '20000', message: 'ok' },
        }), 300);
      });
    }

    return apiService.get<AgeRangeData[]>('/product/api/v1/dashboard/demographics/age');
  }

  /**
   * Fetch report detail data with pagination
   * Endpoint: GET /product/api/v1/dashboard/summary-details (paginated)
   */
  public async fetchReportDetail(page = 1, pageSize = 10): Promise<ReportDetailApiResponse> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(getMockReportDetailData(page, pageSize)), 300);
      });
    }

    return apiService.getRaw<ReportDetailApiResponse>('/product/api/v1/dashboard/summary-details', {
      params: { page, pageSize },
    });
  }
}

